<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LineChart from 'shared/components/charts/LineChart.vue';
import ButtonV4 from 'next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  title: { type: String, required: true },
  labels: { type: Array, default: () => [] },
  // [{ key, label, data, yAxisID? }]; series on the 'cost' axis are drawn on the right
  series: { type: Array, default: () => [] },
  formatCost: { type: Function, default: value => value },
});

const { t } = useI18n();

const COLORS = [
  '#1ACDB8',
  '#5B8DEF',
  '#F5A524',
  '#E5484D',
  '#8E4EC6',
  '#30A46C',
  '#F76B15',
  '#889096',
  '#0090FF',
];

const isCustomizing = ref(false);
const hiddenKeys = ref([]);

watch(
  () => props.series.map(serie => serie.key).join(),
  () => {
    hiddenKeys.value = [];
  }
);

const toggleSerie = key => {
  hiddenKeys.value = hiddenKeys.value.includes(key)
    ? hiddenKeys.value.filter(hidden => hidden !== key)
    : [...hiddenKeys.value, key];
};

const hasCostAxis = computed(() =>
  props.series.some(serie => serie.yAxisID === 'cost')
);

const collection = computed(() => ({
  labels: props.labels,
  datasets: props.series
    .map((serie, index) => ({ serie, color: COLORS[index % COLORS.length] }))
    .filter(({ serie }) => !hiddenKeys.value.includes(serie.key))
    .map(({ serie, color }) => ({
      label: serie.label,
      data: serie.data,
      yAxisID: serie.yAxisID || 'y',
      borderColor: color,
      backgroundColor: color,
      borderDash: serie.yAxisID === 'cost' ? [6, 4] : [],
      tension: 0.3,
      pointRadius: 2,
    })),
}));

const chartOptions = computed(() => {
  if (!hasCostAxis.value) return {};
  return {
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: ({ dataset, raw }) =>
            `${dataset.label}: ${dataset.yAxisID === 'cost' ? props.formatCost(raw) : raw}`,
        },
      },
    },
    scales: {
      x: { grid: { drawOnChartArea: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
      cost: {
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: { callback: value => props.formatCost(value) },
      },
    },
  };
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak bg-n-solid-1"
  >
    <div class="flex justify-between items-center gap-2">
      <span class="text-body-main font-medium text-n-slate-11">
        {{ title }}
      </span>
      <ButtonV4
        sm
        faded
        slate
        icon="i-lucide-sliders-horizontal"
        @click="isCustomizing = !isCustomizing"
      >
        {{ t('INBOX_MGMT.WHATSAPP_INSIGHTS.CHARTS.CUSTOMIZE') }}
      </ButtonV4>
    </div>
    <div v-if="isCustomizing" class="flex flex-wrap gap-x-4 gap-y-2">
      <label
        v-for="serie in series"
        :key="serie.key"
        class="flex gap-2 items-center text-label-small text-n-slate-12 cursor-pointer"
      >
        <Checkbox
          :model-value="!hiddenKeys.includes(serie.key)"
          @change="toggleSerie(serie.key)"
        />
        {{ serie.label }}
      </label>
    </div>
    <div class="h-72">
      <LineChart :collection="collection" :chart-options="chartOptions" />
    </div>
  </div>
</template>
